/**
 * Turn a canonical camelCase metadata key into a human-readable field label for
 * the MetadataManagementTool, so operators see "SEO Title" rather than the raw
 * `slrTktSeoTitle`. Kept separate from metadata-form.utils (data/validation)
 * because this is purely a display concern.
 */

/**
 * Storage-scope prefixes that are noise in a human label — they encode where a
 * key lives (seller / event / ticket-type / ticket-group Ticketure meta), not
 * what it means. Longest first so `mwtix` is stripped before `mw`.
 */
const META_KEY_PREFIXES = ['slrTkt', 'evtTkt', 'ttTkt', 'tgTkt', 'mwtix', 'mw'] as const

/** Words that should read as acronyms / brand casing rather than plain Title Case. */
const META_LABEL_WORDS: Record<string, string> = {
  seo: 'SEO',
  url: 'URL',
  urls: 'URLs',
  id: 'ID',
  ids: 'IDs',
  cta: 'CTA',
  faq: 'FAQ',
  gtm: 'GTM',
  lph: 'LPH',
  api: 'API',
  ui: 'UI',
  bg: 'BG',
  hubspot: 'HubSpot',
  auth0: 'Auth0',
}

/** Title-case one word, honouring the acronym/brand overrides above. */
const humanizeWord = (word: string): string => (
  META_LABEL_WORDS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
)

/** Whether `key` starts with `prefix` used as a scope (i.e. followed by a new word). */
const hasScopePrefix = (key: string, prefix: string): boolean => (
  key.startsWith(prefix)
  && key.length > prefix.length
  && key[prefix.length] === key[prefix.length].toUpperCase()
)

/**
 * Turn a camelCase schema key into a human-readable field label: drop the
 * storage-scope prefix, split on camel-case boundaries, and title-case each
 * word (acronyms like SEO/URL/ID stay uppercase). e.g. `slrTktSeoTitle` →
 * "SEO Title", `maxTicketsForPurchase` → "Max Tickets For Purchase".
 * @param {string} key - the canonical camelCase schema key
 * @returns {string} the display label
 */
export const humanizeMetaKey = (key: string): string => {
  const prefix = META_KEY_PREFIXES.find((candidate) => hasScopePrefix(key, candidate))
  const stripped = prefix ? key.slice(prefix.length) : key

  return stripped
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map(humanizeWord)
    .join(' ')
}
